import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faExclamationTriangle, faArrowRight, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { useLanguage } from '../../contexts/LanguageContext';
import { useTheme } from '../../contexts/ThemeContext';
import webVulnScanService from '../../services/webVulnScanService';
import httpExchangeService from '../../services/httpExchangeService';
import RequestViewer from './RequestViewer';
import ResponseViewer from './ResponseViewer';
import { toast } from 'react-toastify';
import './FindingDetailsModal.css';

const FindingDetailsModal = ({ finding, onClose, onActionCreated }) => {
  const { t } = useLanguage();
  const { darkMode } = useTheme();
  const [activeTab, setActiveTab] = useState('details');
  const [exchange, setExchange] = useState(null);
  const [loadingExchange, setLoadingExchange] = useState(false);

  useEffect(() => {
    if (!finding) return;
    const exchangeId = finding.httpExchanges?.[0] || finding.evidence?.[0]?.exchangeId;
    if (exchangeId && !exchange) {
      setLoadingExchange(true);
      httpExchangeService.getById(exchangeId)
        .then(setExchange)
        .catch(() => {})
        .finally(() => setLoadingExchange(false));
    }
  }, [finding]);

  if (!finding) return null;

  const handleCreateAction = async () => {
    try {
      await webVulnScanService.createActionsFromFindings([finding._id]);
      toast.success(t('webTesting.actionCreated'));
      if (onActionCreated) onActionCreated();
      onClose();
    } catch {
      toast.error(t('errors.actionCreationFailed'));
    }
  };

  const getSeverityColor = (severity) => {
    const colors = {
      critical: '#dc2626',
      high: '#f97316',
      medium: '#eab308',
      low: '#3b82f6',
      informational: '#6b7280'
    };
    return colors[severity] || colors.informational;
  };

  const styles = {
    overlay: {
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0, 0, 0, 0.7)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000, padding: '20px'
    },
    modal: {
      background: darkMode ? '#1f2937' : '#ffffff',
      borderRadius: '12px', width: '100%', maxWidth: '1100px', maxHeight: '90vh',
      overflow: 'hidden',
      boxShadow: darkMode ? '0 20px 25px -5px rgba(0, 0, 0, 0.5)' : '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
      display: 'flex', flexDirection: 'column'
    },
    header: {
      padding: '20px 24px',
      borderBottom: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}`,
      display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
      background: darkMode ? '#111827' : '#f9fafb'
    },
    title: {
      margin: 0, fontSize: '20px', fontWeight: '600',
      color: darkMode ? '#f3f4f6' : '#111827', marginBottom: '8px'
    },
    severityBadge: {
      display: 'inline-flex', alignItems: 'center', gap: '6px',
      padding: '4px 12px', borderRadius: '6px', fontSize: '12px', fontWeight: '600',
      color: '#ffffff', backgroundColor: getSeverityColor(finding.severity),
      textTransform: 'uppercase'
    },
    closeButton: {
      background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer',
      color: darkMode ? '#9ca3af' : '#6b7280', padding: '4px', marginLeft: '16px'
    },
    tabs: {
      display: 'flex', gap: '4px', padding: '0 24px',
      background: darkMode ? '#111827' : '#f9fafb',
      borderBottom: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}`
    },
    tab: (active) => ({
      padding: '12px 20px',
      background: active ? (darkMode ? '#1f2937' : '#ffffff') : 'transparent',
      border: 'none',
      borderBottom: active ? '2px solid #3b82f6' : '2px solid transparent',
      color: active ? (darkMode ? '#f3f4f6' : '#111827') : (darkMode ? '#9ca3af' : '#6b7280'),
      cursor: 'pointer', fontSize: '14px', fontWeight: active ? '600' : '400',
      transition: 'all 0.2s'
    }),
    content: { flex: 1, overflow: 'auto', padding: '24px' },
    section: { marginBottom: '24px' },
    sectionTitle: {
      fontSize: '14px', fontWeight: '600',
      color: darkMode ? '#9ca3af' : '#6b7280',
      textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '12px'
    },
    label: { fontSize: '12px', color: darkMode ? '#9ca3af' : '#6b7280', marginBottom: '4px', fontWeight: '500' },
    value: { fontSize: '14px', color: darkMode ? '#f3f4f6' : '#111827', wordBreak: 'break-word' },
    code: {
      background: darkMode ? '#111827' : '#f3f4f6',
      border: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}`,
      borderRadius: '6px', padding: '16px', fontFamily: "'Courier New', monospace",
      fontSize: '13px', color: darkMode ? '#d1d5db' : '#374151',
      whiteSpace: 'pre-wrap', wordBreak: 'break-all', maxHeight: '400px', overflow: 'auto'
    },
    footer: {
      padding: '16px 24px',
      borderTop: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}`,
      background: darkMode ? '#111827' : '#f9fafb',
      display: 'flex', justifyContent: 'flex-end', gap: '12px'
    },
    actionButton: {
      padding: '10px 20px', background: '#3b82f6', color: '#ffffff', border: 'none',
      borderRadius: '6px', fontSize: '14px', fontWeight: '500', cursor: 'pointer',
      display: 'flex', alignItems: 'center', gap: '8px'
    },
    cancelButton: {
      padding: '10px 20px',
      background: darkMode ? '#374151' : '#e5e7eb',
      color: darkMode ? '#f3f4f6' : '#111827',
      border: 'none', borderRadius: '6px', fontSize: '14px', fontWeight: '500', cursor: 'pointer'
    }
  };

  return (
    <div className="modal-overlay" style={styles.overlay} onClick={onClose}>
      <div className="modal" style={styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="modal-header" style={styles.header}>
          <div style={{ flex: 1 }}>
            <h2 style={styles.title}>{finding.title}</h2>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <span style={styles.severityBadge}>
                <FontAwesomeIcon icon={faExclamationTriangle} />
                {finding.severity}
              </span>
              <span style={{
                fontSize: '12px', color: darkMode ? '#9ca3af' : '#6b7280',
                padding: '4px 12px', background: darkMode ? '#374151' : '#f3f4f6', borderRadius: '6px'
              }}>
                {finding.confidence}
              </span>
            </div>
          </div>
          <button style={styles.closeButton} onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {/* Tabs */}
        <div style={styles.tabs}>
          <button style={styles.tab(activeTab === 'details')} onClick={() => setActiveTab('details')}>
            {t('common.details')}
          </button>
          <button style={styles.tab(activeTab === 'request')} onClick={() => setActiveTab('request')}>
            {t('webTesting.request')}
          </button>
          <button style={styles.tab(activeTab === 'evidence')} onClick={() => setActiveTab('evidence')}>
            {t('webTesting.evidence')}
          </button>
        </div>

        {/* Content */}
        <div style={styles.content}>
          {activeTab === 'details' && (
            <>
              <div style={styles.section}>
                <div style={styles.sectionTitle}>{t('webTesting.vulnerability')}</div>
                <div style={{ marginBottom: '12px' }}>
                  <div style={styles.label}>{t('common.description')}</div>
                  <div style={styles.value}>{finding.description}</div>
                </div>
                <div style={{ marginBottom: '12px' }}>
                  <div style={styles.label}>{t('webTesting.url')}</div>
                  <div style={{ ...styles.value, fontFamily: "'Courier New', monospace", fontSize: '13px' }}>
                    {finding.endpoint?.url}
                  </div>
                </div>
                <div style={{ marginBottom: '12px' }}>
                  <div style={styles.label}>{t('webTesting.parameter')}</div>
                  <div style={styles.value}>{finding.parameter?.name || '-'}</div>
                </div>
                <div style={{ marginBottom: '12px' }}>
                  <div style={styles.label}>{t('webTesting.parameterLocation')}</div>
                  <div style={styles.value}>{finding.parameter?.location || finding.parameter?.type || '-'}</div>
                </div>
                {finding.parameter?.injectedPayload && (
                  <div style={{ marginBottom: '12px' }}>
                    <div style={styles.label}>{t('webTesting.injectedPayload')}</div>
                    <div style={styles.code}>{finding.parameter.injectedPayload}</div>
                  </div>
                )}
              </div>

              {finding.mitreMapping && finding.mitreMapping.length > 0 && (
                <div style={styles.section}>
                  <div style={styles.sectionTitle}>{t('mitre.attackMapping')}</div>
                  {finding.mitreMapping.map((m, idx) => (
                    <div key={idx} style={{ marginBottom: '8px' }}>
                      <span style={{
                        background: darkMode ? '#1e3a5f' : '#eff6ff',
                        color: darkMode ? '#60a5fa' : '#1e40af',
                        padding: '4px 8px', borderRadius: '4px', fontSize: '13px', fontWeight: '500', marginRight: '8px'
                      }}>
                        {m.techniqueId}
                      </span>
                      <span style={{ ...styles.value, fontSize: '13px' }}>{m.techniqueName}</span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {activeTab === 'request' && (
            <div style={styles.section}>
              {/* Injected payload */}
              {(finding.parameter?.injectedPayload || finding.payload) && (
                <div style={{ marginBottom: '20px' }}>
                  <div style={styles.sectionTitle}>{t('webTesting.injectedPayload')}</div>
                  <div style={styles.code}>
                    {finding.parameter?.injectedPayload || finding.payload}
                  </div>
                </div>
              )}

              {/* Full request/response from linked HttpExchange */}
              {loadingExchange && (
                <div style={{ textAlign: 'center', padding: '20px', color: darkMode ? '#9ca3af' : '#6b7280' }}>
                  <FontAwesomeIcon icon={faSpinner} spin /> Loading request...
                </div>
              )}
              {exchange && (
                <div>
                  <div style={styles.sectionTitle}>HTTP Request / Response</div>
                  <div style={{ display: 'flex', gap: '1px', background: darkMode ? '#374151' : '#e5e7eb', borderRadius: '8px', overflow: 'hidden', height: '400px' }}>
                    <div style={{ flex: 1, overflow: 'auto', background: darkMode ? '#1f2937' : '#ffffff' }}>
                      <RequestViewer exchange={exchange} isDarkMode={darkMode} />
                    </div>
                    <div style={{ flex: 1, overflow: 'auto', background: darkMode ? '#1f2937' : '#ffffff' }}>
                      <ResponseViewer exchange={exchange} isDarkMode={darkMode} />
                    </div>
                  </div>
                </div>
              )}
              {!loadingExchange && !exchange && !finding.parameter?.injectedPayload && !finding.payload && (
                <div style={{ color: darkMode ? '#6b7280' : '#9ca3af', textAlign: 'center', padding: '20px' }}>
                  No request data available for this finding.
                </div>
              )}
            </div>
          )}

          {activeTab === 'evidence' && (
            <div style={styles.section}>
              <div style={styles.sectionTitle}>{t('webTesting.detectionEvidence')}</div>
              {/* Evidence entries */}
              {finding.evidence && finding.evidence.length > 0 ? (
                finding.evidence.map((ev, idx) => (
                  <div key={idx} style={{ marginBottom: '16px' }}>
                    {ev.title && (
                      <div style={{ ...styles.label, fontWeight: '600', marginBottom: '8px' }}>{ev.title}</div>
                    )}
                    {ev.content && (
                      <div style={styles.code}>{ev.content}</div>
                    )}
                    {ev.summary && (
                      <div style={styles.value}>{ev.summary}</div>
                    )}
                    {ev.responseSnippet && (
                      <div style={{ marginTop: '8px' }}>
                        <div style={styles.label}>{t('webTesting.responseSnippet')}</div>
                        <div style={styles.code}>{ev.responseSnippet}</div>
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <div style={styles.value}>
                  {finding.detectionReason || finding.evidenceMarker || 'No evidence details available.'}
                </div>
              )}

              {/* Evidence marker */}
              {finding.evidenceMarker && (
                <div style={{ marginTop: '16px' }}>
                  <div style={styles.label}>Detection Pattern</div>
                  <div style={styles.code}>{finding.evidenceMarker}</div>
                </div>
              )}

              {/* Response from linked exchange */}
              {exchange && (
                <div style={{ marginTop: '20px' }}>
                  <div style={styles.sectionTitle}>Response (proof)</div>
                  <div style={{ borderRadius: '8px', overflow: 'hidden', height: '300px', border: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}` }}>
                    <ResponseViewer
                      exchange={exchange}
                      isDarkMode={darkMode}
                      searchRegex={finding.evidenceMarker ? new RegExp(finding.evidenceMarker.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi') : null}
                    />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={styles.footer}>
          <button style={styles.cancelButton} onClick={onClose}>{t('common.close')}</button>
          <button style={styles.actionButton} onClick={handleCreateAction}>
            <FontAwesomeIcon icon={faArrowRight} />
            {t('webTesting.createRedTeamAction')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FindingDetailsModal;
